import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import Header from '../Header/Header';
import ModuleGate from '../ModuleGate';
import FederationGate from '../FederationGate';
import './Layout.css';
import AppFooter from '@/components/Layout/AppFooter';
import { AppPageHeading, PageHeadingBar } from '@/components/Layout/AppPageHeading';
import { PageHeadingProvider } from '@/components/Layout/PageHeadingContext';
import { AppHeaderBreadcrumbs } from '@/components/EaShell/AppHeaderBreadcrumbs';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import i18n from '../../i18n';
import ProtectedRoute from '../ProtectedRoute';
import RoleBasedRoute from '../RoleBasedRoute';
import Notification from '../Notification/Notification';
import PlaceholderPage from '../../pages/ProcessPolicyPlaceholder/PlaceholderPage';
import RcsaCatalogCompliance from '@/pages/RCSACompliance/rcsa';
import ExportSurvey from '@/pages/AllSurveys/ExportSurvey';
import TicketsSupCompliance from '@/pages/Tickets_Sup_Compliance/Tickets';
import StandardsSupCompliance from '@/pages/references_Sup_Compliance/references';
import CertificationsListSup from '@/pages/certifications_Sup_compliance/ListCertifications';
import IntialAssessmentSup from '@/pages/IntialAssessment_Sup/intialAssessment';
import AnnouncementSup from '@/pages/Announcement_Sup_Compliance/Announcement';
import DashboardSup from '@/pages/dashboard_Sup/Dashboard';
import ReportSupCompliance from '@/pages/ReportSupCompliance/Report';
import InstitutionSupHome from '@/pages/Institution/home';
import ChildTenantFieldOperations from '@/pages/Institution/ChildTenantFieldOperations';
import CertificateSigningApp from '@/pages/certifications_Sup_compliance/AssignCertificaties';
import CategoryTable from '@/pages/category/category';
import SurveyCategoryQuestionManager from '@/pages/SurveyCategoryQuestion/SurveyCategoryQuestion';
import ViewQuestionsTable from '@/pages/QuestionAssessment_Sup_Comly/ViewQuestionAssessment';
import CreateQuestionForm from '@/pages/QuestionAssessment_Sup_Comly/createQuestionAssessment';

// Lazy-loaded page components (code splitting to reduce initial bundle size)
const ComplianceStatus = React.lazy(() => import('../../pages/compliance/complianceStatus/compliance_status'));
const UserManage = React.lazy(() => import('src/pages/User/UserManage'));
const UserPermissionManage = React.lazy(() => import('src/pages/User/UserPermissionManage'));
const PermissionGroupPageManage = React.lazy(() => import('src/pages/Permission/PermissionGroupPageManage'));
const TaskUserCalender = React.lazy(() => import('src/pages/DashboardImplementor/component/TaskUserCalender'));
const AnnouncementTable = React.lazy(() => import('src/pages/Announcement/announcement'));
const TenantTree = React.lazy(() => import('src/pages/Tenant/TenantTree'));
const TenantTickets = React.lazy(() => import('src/pages/Tenant/TenantTickets'));
const TenantSettings = React.lazy(() => import('src/pages/Tenant/TenantSettings'));
const TenantStandards = React.lazy(() => import('src/pages/Tenant/TenantStandards'));
const TenantDetail = React.lazy(() => import('src/pages/Tenant/TenantDetail'));
const TenantMeDebug = React.lazy(() => import('src/pages/Debug/TenantMeDebug'));
// Phase 4 — Tenant Owner Admin (subtree management, memberships, roll-ups)
const TenantAdminSubtree = React.lazy(() => import('src/pages/TenantAdmin/SubtreePage'));
const TenantAdminMemberships = React.lazy(() => import('src/pages/TenantAdmin/MembershipsPage'));
const TenantAdminSubTenantNew = React.lazy(() => import('src/pages/TenantAdmin/SubTenantNewPage'));
const TenantAdminSubTenantDetail = React.lazy(() => import('src/pages/TenantAdmin/SubTenantDetailPage'));
const TenantAdminRollup = React.lazy(() => import('src/pages/TenantAdmin/RollupDashboardPage'));
const TenantAdminRollupFindings = React.lazy(() => import('src/pages/TenantAdmin/RollupFindingsPage'));
const TenantAdminRollupSpend = React.lazy(() => import('src/pages/TenantAdmin/RollupSpendPage'));
const TenantAdminInsights = React.lazy(() => import('src/pages/TenantAdmin/InsightsPage'));
// Pianat Admin (platform-operator console) was extracted into its own standalone
// app (pianat_admin_frontend) so it is never shipped to customer browsers.
// Phase 2 — Client Portal self-service (dashboard, self-assessment, AI coach)
const ClientPortalDashboard = React.lazy(() => import('src/pages/ClientPortal/DashboardPage'));
const ClientPortalSelfAssessments = React.lazy(() => import('src/pages/ClientPortal/SelfAssessmentListPage'));
const ClientPortalSelfAssessmentNew = React.lazy(() => import('src/pages/ClientPortal/SelfAssessmentNewPage'));
const ClientPortalSelfAssessmentDetail = React.lazy(() => import('src/pages/ClientPortal/SelfAssessmentDetailPage'));
const ClientPortalSelfAssessmentAi = React.lazy(() => import('src/pages/ClientPortal/SelfAssessmentAiSuggestionsPage'));
const ClientPortalFrameworks = React.lazy(() => import('src/pages/ClientPortal/FrameworksPage'));
const ClientPortalNotifications = React.lazy(() => import('src/pages/ClientPortal/NotificationsPage'));
const ClientPortalProfile = React.lazy(() => import('src/pages/ClientPortal/ProfilePage'));
// TrustLens — light-tier onboarding (Posture Cascade L1→L2→L3→L4)
const TrustLensPostureLayout = React.lazy(() => import('src/pages/TrustLens/PostureContext'));
const TrustLensPostureL1 = React.lazy(() => import('src/pages/TrustLens/PostureL1Page'));
const TrustLensPostureL2 = React.lazy(() => import('src/pages/TrustLens/PostureL2Page'));
const TrustLensPostureL3 = React.lazy(() => import('src/pages/TrustLens/PostureL3Page'));
const TrustLensPostureL4 = React.lazy(() => import('src/pages/TrustLens/PostureL4Page'));
const TrustLensPostureL5 = React.lazy(() => import('src/pages/TrustLens/PostureL5Page'));
const TrustLensDashboard = React.lazy(() => import('src/pages/TrustLens/AssessmentDashboardPage'));
const TrustLensProfile = React.lazy(() => import('src/pages/TrustLens/ProfileContextPage'));
const TrustLensSectionsTeam = React.lazy(() => import('src/pages/TrustLens/SectionsTeamPage'));
const TrustLensReport = React.lazy(() => import('src/pages/TrustLens/ReportPage'));
const TrustLensInvitations = React.lazy(() => import('src/pages/TrustLens/InvitationsPage'));
// Phase 5 — Sub-Tenant Operations (inter-branch collaboration, branch ops agent)
const SubTenantInterBranch = React.lazy(() => import('src/pages/SubTenant/InterBranchPage'));
const SubTenantInterBranchNew = React.lazy(() => import('src/pages/SubTenant/InterBranchNewPage'));
const SubTenantInterBranchThread = React.lazy(() => import('src/pages/SubTenant/InterBranchThreadPage'));
const SubTenantBranchOps = React.lazy(() => import('src/pages/SubTenant/BranchOpsPage'));
const FederationHub = React.lazy(() => import('src/pages/Federation/FederationHub'));
const FederationDetail = React.lazy(() => import('src/pages/Federation/FederationDetail'));
const ServiceProviderRequest = React.lazy(() => import('src/pages/Federation/ServiceProviderRequest'));
const EvidenceVaultList = React.lazy(() => import('src/pages/EvidenceVault/EvidenceVaultList'));
const EvidenceArtifactDetail = React.lazy(() => import('src/pages/EvidenceVault/EvidenceArtifactDetail'));
const TrustCenter = React.lazy(() => import('src/pages/TrustCenter/TrustCenter'));
const TrustCenterPublic = React.lazy(() => import('src/pages/TrustCenter/TrustCenterPublic'));
const PlaybookLibrary = React.lazy(() => import('src/pages/Playbooks/PlaybookLibrary'));
const PlaybookDetailPage = React.lazy(() => import('src/pages/Playbooks/PlaybookDetail'));
const PlaybookAuthor = React.lazy(() => import('src/pages/Playbooks/PlaybookAuthor'));
const LlmRouting = React.lazy(() => import('src/pages/Tenant/LlmRouting'));
const PartnerLedgerStatement = React.lazy(() => import('src/pages/PartnerLedger/Statement'));
// Admin Partners pages (PartnersDirectory, Scorecard, Onboarding,
// PartnerDetail, PartnerConflicts) removed alongside partnersSeed.ts.
// They were demo-only with no real backend. When platform-admin
// endpoints exist (/api/admin/partners/*), rebuild against them.
// Partner Portal moved to the standalone partner_portal_frontend app — its
// pages (MyEngagements, MyPractitioners, MyComplianceStatus, MyEarnings) and
// /partner-portal/* routes were removed from this customer app.
// PartnerLogin (demo identity switcher) removed — superseded by real auth in
// src/pages/login/login.tsx. Spec moves "Switch Identity" into the header
// dropdown for users with multi-partner membership; tracked as a follow-up.
const RiskList = React.lazy(() => import('src/pages/Risk/RiskList'));
const RiskDetail = React.lazy(() => import('src/pages/Risk/RiskDetail'));
const RiskForm = React.lazy(() => import('src/pages/Risk/RiskForm'));
const ComplianceLinksDetail = React.lazy(() => import('src/pages/compliance/ComplianceLinksDetail'));
const CapabilityLinksDetail = React.lazy(() => import('src/pages/Capability/CapabilityLinksDetail'));
const RoleDashboard = React.lazy(() => import('src/pages/Dashboards/RoleDashboard'));
const CertificationsList = React.lazy(() => import('src/pages/certifications/ListCertifications'));
const CreateQuestion = React.lazy(() => import('src/pages/Surveys_Questions/BankQuestions/CreateQuestion'));
const CreateSurvey = React.lazy(() => import('src/pages/AllSurveys/CreateSurvey'));
const Surveys = React.lazy(() => import('src/pages/Surveys_Questions/Surveys/Surveys'));
const QuestionBankTable = React.lazy(() => import('../../pages/Surveys_Questions/BankQuestions/questionCatalog'));

const UpdateSurvey = React.lazy(() => import('src/pages/AllSurveys/UpdateSurvey'));
const UpdateQuestion = React.lazy(() => import('src/pages/Surveys_Questions/BankQuestions/UpdateQuestion'));
const PermissionPages = React.lazy(() => import('src/pages/PermissionLoginPages/PermissionPages'));
const ImpactAreaTable = React.lazy(() => import('src/pages/ImpactArea/ViewImpact'));
const CreateImpact = React.lazy(() => import('src/pages/ImpactArea/CreateImpact'));
const UpdateImpact = React.lazy(() => import('src/pages/ImpactArea/update/updateImpact'));
const Register = React.lazy(() => import('src/pages/register/Register'));
const CreateFunctionPage = React.lazy(() => import('src/pages/FunctionUser/Function'));
const ServicesPage = React.lazy(() => import('src/pages/Services/ServicesPage'));

const ReplayQuestion = React.lazy(() => import('src/pages/AllSurveys/ReplayQuestion'));
const QuestionControlTree = React.lazy(() => import('src/pages/QuestionControl/QuestionControlTree'));
const TeamOnboarding = React.lazy(() => import('src/pages/invitationUser/inviteTeam'));
const ObjectiveModal = React.lazy(() => import('src/pages/Objective/objectiveModal'));
const UnifiedControlLandscape = React.lazy(() => import('src/pages/frameWork/frameWork'));
const AssessmentScore = React.lazy(() => import('src/pages/frameWork/AssessmentScore'));
const OrganizationChart = React.lazy(() => import('src/pages/OrganizationChart/OrganizationChart'));
const FileManager = React.lazy(() => import('src/pages/OnBoxMessage/viewAllMessage'));
const ControlUpdatePage = React.lazy(() => import('src/pages/controls/update/[id]'));
const AssetsList = React.lazy(() => import('src/pages/AssetsManagement/AssetsList'));
const CreateAsset = React.lazy(() => import('src/pages/AssetsManagement/CreateAsset'));
const AssetDetails = React.lazy(() => import('src/pages/AssetsManagement/AssetDetails'));
const EditAsset = React.lazy(() => import('src/pages/AssetsManagement/EditAsset'));
const DataSourceList = React.lazy(() => import('src/pages/dataresource/index'));
const DataSourceDetails = React.lazy(() => import('src/pages/dataresource/DataSourceDetails'));
const UpdateDataSource = React.lazy(() => import('src/pages/dataresource/update/UpdateDataSource'));
const DocumentList = React.lazy(() => import('src/pages/DocumentManagement/DocumentList'));
const DocumentUpload = React.lazy(() => import('src/pages/DocumentManagement/DocumentUpload'));
const DocumentDetails = React.lazy(() => import('src/pages/DocumentManagement/DocumentDetails'));
const DocumentEdit = React.lazy(() => import('src/pages/DocumentManagement/DocumentEdit'));
const DocumentCategoriesPage = React.lazy(() => import('src/pages/DocumentManagement/DocumentCategoriesPage'));
const ServicesStatusPage = React.lazy(() => import('src/pages/Configuration/ServicesStatusPage'));
const LifecycleStagesPage = React.lazy(() => import('src/pages/Configuration/LifecycleStagesPage'));
const CriticalityPage = React.lazy(() => import('src/pages/Configuration/CriticalityPage'));
const AssetTypesPage = React.lazy(() => import('src/pages/Configuration/AssetTypesPage'));
const AssetClassificationsPage = React.lazy(() => import('src/pages/Configuration/AssetClassificationsPage'));
const ComplianceDashboard = React.lazy(() => import('src/pages/DocumentManagement/ComplianceDashboard'));
const CatalogsPage = React.lazy(() => import('src/pages/Catalogs/CatalogsPage'));
const SopCatalogPage = React.lazy(() => import('src/pages/sopCollection/SopCatalogPage'));
const SopMastersPage = React.lazy(() => import('src/pages/sopCollection/SopMastersPage'));
const RiskControlsMatrixPage = React.lazy(() => import('src/pages/dashboard/RiskControlsMatrixPage'));
// Receivables Hub — unified tabbed container covering executive dashboards,
// live collections operations, and the cycle-time simulator. Deep links to
// the legacy routes below still resolve here via the `initialTab` prop.
const ReceivablesHubPage = React.lazy(() => import('src/pages/analytics/ReceivablesHubPage'));
const ServicesCatalogPage = React.lazy(() => import('src/pages/Catalogs/ServicesCatalogPage'));
const BusinessModelInventoryPage = React.lazy(() => import('src/pages/business-model-inventory/index'));
const ServiceCatalogCreatePage = React.lazy(() => import('src/pages/business-model-inventory/create'));
const DocumentStructurePage = React.lazy(() => import('src/pages/DocumentManagement/DocumentStructurePage'));
// Legacy /demo-partner/* lazy imports removed — superseded by /partner-portal/*
// per the Partner Portal production spec (Fix 2).

// Partner Portal pages and their React.lazy imports were removed — the Partner
// Portal now lives in the standalone partner_portal_frontend app.
// Client Portal pages — the assessed organisation's side (call a partner,
// send assessment requests, message + share files on engagements).
const ClientPortalPartners = React.lazy(() => import('src/pages/ClientPortal/PartnersPage'));
const ClientPortalRequests = React.lazy(() => import('src/pages/ClientPortal/RequestsPage'));
const ClientPortalEngagements = React.lazy(() => import('src/pages/ClientPortal/EngagementsPage'));
const ClientPortalEngagementWorkspace = React.lazy(() => import('src/pages/ClientPortal/EngagementWorkspacePage'));
const ControlsIndex = React.lazy(() => import('src/pages/controls/index'));
const CreateControlPage = React.lazy(() => import('src/pages/controls/create'));
const ControlPage = React.lazy(() => import('src/pages/controls/[id]'));
const ReportingPage = React.lazy(() => import('src/pages/Reporting/ReportingPage'));
const UpdateAssessment = React.lazy(() => import('src/pages/Assessment/updateAssessment'));
const CreateProcessPage = React.lazy(() => import('src/pages/processes/create'));
const CreateSubBusinessUnitPage = React.lazy(() => import('src/pages/Sub-Business-Unit/create'));
const CreateRootCausePage = React.lazy(() => import('src/pages/rootCauses/create'));
const CreateRiskCategoryPage = React.lazy(() => import('src/pages/riskCategories/create'));
const CreateSubFunctionPage = React.lazy(() => import('../../pages/sub_functions/create'));
const CreateSystemPage = React.lazy(() => import('src/pages/systems/create'));
const CreateJobTitlePage = React.lazy(() => import('src/pages/jobTitles/create'));
const CreateEventTypePage = React.lazy(() => import('src/pages/incidentEventsType/create'));
const CreateDiscoveredTypePage = React.lazy(() => import('src/pages/discoveredTypes/create'));
const CreateCurrencyPage = React.lazy(() => import('src/pages/currencies/create'));
const CreateActivityPage = React.lazy(() => import('src/pages/activities/create'));
const ControlReferencePage = React.lazy(() => import('src/pages/ControlRefrence/create'));
const FinancialImpactsPage = React.lazy(() => import('src/pages/financialImpacts/create'));
const EventTypePage = React.lazy(() => import('src/pages/eventsType/create'));
const CreateAccountType = React.lazy(() => import('src/pages/AccountType/create'));

const ProcessBuilder = React.lazy(() => import('../../pages/ProcessBuilder'));
const Instances = React.lazy(() => import('../../pages/Instances'));
const InstanceDetail = React.lazy(() => import('../../pages/InstanceDetail'));
const TaskInbox = React.lazy(() => import('../../pages/TaskInbox'));
const LiveMonitor = React.lazy(() => import('../../pages/LiveMonitor'));
const AgentConfig = React.lazy(() => import('../../pages/AgentConfig'));
const UserGuide = React.lazy(() => import('../../pages/UserGuide'));
const PolicyLibrary = React.lazy(() => import('../../pages/policy/PolicyLibrary'));
const PolicyApprovals = React.lazy(() => import('../../pages/policy/PolicyApprovals'));
const PolicyAttestation = React.lazy(() => import('../../pages/policy/PolicyAttestation'));
const PolicyVersionControl = React.lazy(() => import('../../pages/policy/PolicyVersionControl'));
const RegulatoryObligations = React.lazy(() => import('../../pages/policy/RegulatoryObligations'));
const PolicyAudit = React.lazy(() => import('../../pages/policy/PolicyAudit'));
const ActivityLogPage = React.lazy(() => import('../../pages/audit/ActivityLogPage'));
const IamsDashboard = React.lazy(() => import('../../pages/iams/IamsHome'));
const IamsLiveCanvas = React.lazy(() => import('../../pages/iams/LiveCanvas'));
const IamsAuditUniverse = React.lazy(() => import('../../pages/iams/AuditUniverse'));
const IamsPlanComposer = React.lazy(() => import('../../pages/iams/AuditPlanComposer'));
const IamsFieldworkCanvas = React.lazy(() => import('../../pages/iams/FieldworkCanvas'));
const IamsFindingDetail = React.lazy(() => import('../../pages/iams/FindingDetail'));
const IamsAuditTrailViewer = React.lazy(() => import('../../pages/iams/AuditTrailViewer'));
const IamsAnomalyTriage = React.lazy(() => import('../../pages/iams/AnomalyTriage'));
const IamsRiskUpdateQueue = React.lazy(() => import('../../pages/iams/RiskUpdateQueue'));
const IamsAdmin = React.lazy(() => import('../../pages/iams/IamsAdmin'));
const IamsEngReportBuilder = React.lazy(() => import('../../pages/iams/ReportBuilder').then((m) => ({ default: m.EngagementReportBuilder })));
const IamsAcPackBuilder = React.lazy(() => import('../../pages/iams/ReportBuilder').then((m) => ({ default: m.AcPackBuilder })));
const IamsBoardPackBuilder = React.lazy(() => import('../../pages/iams/ReportBuilder').then((m) => ({ default: m.BoardPackBuilder })));
const IamsAcDashboard = React.lazy(() => import('../../pages/iams/AcDashboard'));
const IamsAuditeePortal = React.lazy(() => import('../../pages/iams/AuditeePortal'));
const IamsAuditeeRequests = React.lazy(() => import('../../pages/iams/AuditeeRequestTracker'));
const IamsAuditeeNotify = React.lazy(() => import('../../pages/iams/AuditeeNotifyBuilder'));
const IamsEvidenceLocker = React.lazy(() => import('../../pages/iams/EvidenceLocker'));
const IamsCapacityPlanner = React.lazy(() => import('../../pages/iams/CapacityPlanner'));
const IamsWorkingPaperEditor = React.lazy(() => import('../../pages/iams/WorkingPaperEditor'));
const IamsTestPlanComposer = React.lazy(() => import('../../pages/iams/TestPlanComposer'));
const IamsContinuousMonitor = React.lazy(() => import('../../pages/iams/ContinuousMonitor'));
const IamsAcPlanReview = React.lazy(() => import('../../pages/iams/AcPlanReview'));
const IamsCopilotConnector = React.lazy(() => import('../../pages/iams/CopilotConnector'));
const IamsTrustCenter = React.lazy(() => import('../../pages/iams/TrustCenter'));
const IamsBulkImport = React.lazy(() => import('../../pages/iams/BulkImport'));
const IamsChecklists = React.lazy(() => import('../../pages/iams/Checklists'));
const IamsRcLibrary = React.lazy(() => import('../../pages/iams/RcLibrary'));
const IamsResourceProfile = React.lazy(() => import('../../pages/iams/ResourceProfile'));
const IamsWorkflowDesigner = React.lazy(() => import('../../pages/iams/WorkflowDesigner'));
const IamsAnalytics = React.lazy(() => import('../../pages/iams/Analytics'));
const IamsScenarioPlanner = React.lazy(() => import('../../pages/iams/ScenarioPlanner'));
const IamsEngagements = React.lazy(() => import('../../pages/iams/Engagements'));
const IamsEngagementDetail = React.lazy(() => import('../../pages/iams/EngagementDetail'));
const IamsReviewQueue = React.lazy(() => import('../../pages/iams/ReviewQueue'));
const IamsFindings = React.lazy(() => import('../../pages/iams/Findings'));
const IamsActionPlans = React.lazy(() => import('../../pages/iams/ActionPlans'));
const IamsAgentRuns = React.lazy(() => import('../../pages/iams/AgentRuns'));
const PolicyCategories = React.lazy(() => import('../../pages/policy/PolicyCategories'));
const PolicySettings = React.lazy(() => import('../../pages/policy/PolicySettings'));
const ProcessInstanceViewer = React.lazy(() => import('../../pages/ProcessInstanceViewer'));
const ProcessDetailPage = React.lazy(() => import('../../pages/process/ProcessDetailPage'));
const ProcessModeler = React.lazy(() => import('../../pages/ProcessModeler'));
const MetricsDashboard = React.lazy(() => import('../../pages/MetricsDashboard'));
const MetricDefinitions = React.lazy(() => import('../../pages/MetricDefinitions'));
const ProcessAnalytics = React.lazy(() => import('../../pages/ProcessAnalytics'));

function RedirectProcessInstanceId() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/instances/${id}` : '/instances'} replace />;
}

function RedirectAssetsToResources() {
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const path = location.pathname;
  if (path === '/assets') return <Navigate to="/catalogs/resources" replace />;
  if (path === '/assets/create') return <Navigate to="/catalogs/resources/create" replace />;
  if (path.startsWith('/assets/edit/') && id) return <Navigate to={`/catalogs/resources/edit/${id}`} replace />;
  if (path.startsWith('/assets/') && id) return <Navigate to={`/catalogs/resources/${id}`} replace />;
  return <Navigate to="/catalogs/resources" replace />;
}

const EADashboard = React.lazy(() => import('../../pages/EnterpriseArchitecture/EADashboard'));
const EAPrinciples = React.lazy(() => import('../../pages/EnterpriseArchitecture/EAPrinciples'));
const EAApplications = React.lazy(() => import('../../pages/EnterpriseArchitecture/EAApplications'));
const EAWorkPackages = React.lazy(() => import('../../pages/EnterpriseArchitecture/EAWorkPackages'));
const EAScopeItems = React.lazy(() => import('../../pages/EnterpriseArchitecture/EAScopeItems'));
const EARelationsTree = React.lazy(() => import('../../pages/EnterpriseArchitecture/EARelationsTree'));
const EaCanvasModule = React.lazy(() => import('@/enterprise/EaCanvasModule'));
const DataInventoryDashboard = React.lazy(() => import('../../pages/DataInventory/DataInventoryDashboard'));
const CatalogsDashboard = React.lazy(() => import('../../pages/Catalogs/CatalogsDashboard'));
const ComplianceModuleDashboard = React.lazy(() => import('../../pages/compliance/ComplianceModuleDashboard'));
const ConfigurationDashboard = React.lazy(() => import('../../pages/Configuration/ConfigurationDashboard'));

const ViewControl = React.lazy(() => import('../../pages/compliance/assessment/viewControl'));
const CreateCompliance = React.lazy(() => import('../../pages/compliance/createCompliance/CreateCompliance'));
const UpdateCompliance = React.lazy(() => import('../../pages/compliance/editCompliance/UpdateCompliance'));
const Compliance = React.lazy(() => import('../../pages/compliance/compliance/Compliance'));
const InformationCompliance = React.lazy(() => import('../../pages/compliance/info/informationCompliance'));
const Dashboard = React.lazy(() => import('../../pages/dashboard/Dashboard'));
const References = React.lazy(() => import('../../pages/referenes/references'));
const Login = React.lazy(() => import('../../pages/login/login'));
const LogoutClear = React.lazy(() => import('../../pages/logout-clear/LogoutClear'));

const Tickets = React.lazy(() => import('../../pages/Tickets/Tickets'));
// Subscribe & Zoho subscribe - commented out
// const SubscriptionPage = React.lazy(() => import('../../pages/subscription/SubscriptionPage'));
// const RenewSubscriptionPage = React.lazy(() => import('../../pages/renew-subscription/RenewSubscriptionPage'));




const Main: React.FC = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [appLanguage, setAppLanguage] = useState<'en' | 'ar'>(() => (i18n.language === 'ar' ? 'ar' : 'en'));
    const location = useLocation();
    
    // Example: Set direction dynamically
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Determine if the current route is the login/register page
    // Note: pathname is relative to basename, so '/login' is correct
    // Also handle legacy '/compliance/login' URL by treating it as login
    // Public Trust Center pages also strip chrome so visitors (regulators,
    // partners, insurers) see a clean, self-contained page.
    const isLoginPage =
      location.pathname === '/login' ||
      location.pathname === '/register' ||
      location.pathname === '/compliance/login' ||
      location.pathname.startsWith('/trust/');

    const handleLanguageChange = (lng: string) => {
        setAppLanguage(lng === 'ar' ? 'ar' : 'en');
    };

    useEffect(() => {
        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);
//console.log(userRole,"userRoleuserRoleuserRole")
    return (
        <div className={`layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
            {!isLoginPage &&  <Sidebar 
              isOpen={isSidebarOpen} 
              onToggle={() => setIsSidebarOpen(!isSidebarOpen)} 
              language={appLanguage} 
            />}
            <div className={`${!isLoginPage ? 'main-content' : 'main-content-login w-100'}`}>
                <ToastContainer />
                {!isLoginPage && (
                  <>
                    {/* Subscribe & Zoho subscribe - commented out
                    <SubscriptionAlert />
                    */}
                    <Header onMobileToggle={toggleSidebar}  className='w-100 z-100' />
                  </>
                )}
                <PageHeadingProvider>
                <div className="d-flex flex-column flex-grow-1 min-h-0 main-content-body" style={{ minHeight: 0 }}>
                {!isLoginPage && (
                  <PageHeadingBar>
                    <div className="main-breadcrumb-bar flex-shrink-0">
                      <div className="main-breadcrumb-bar__inner">
                        <AppHeaderBreadcrumbs className="w-full" />
                        <AppPageHeading />
                      </div>
                    </div>
                  </PageHeadingBar>
                )}
                <div className="main-content-scroll">
                <React.Suspense fallback={
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                }>
                    <ModuleGate>
                    <FederationGate>
                    <Routes>

                    <Route path="/" element={
                          <ProtectedRoute>
                            <RoleBasedRoute />
                          </ProtectedRoute>
                        } />
                        <Route path="/reporting" element={
                          <ProtectedRoute>
                            <ReportingPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/info" element={
                          <ProtectedRoute>
                            <InformationCompliance />
                          </ProtectedRoute>
                        } />
                         <Route path="/announcements" element={
                          <ProtectedRoute>
                            <AnnouncementTable/>
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances/dashboard" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <ComplianceModuleDashboard />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances" element={
                          <ProtectedRoute>
                            <Compliance />
                          </ProtectedRoute>
                        } />
                        <Route path="/standards" element={
                          <ProtectedRoute>
                            <References />
                          </ProtectedRoute>
                        } />
                        <Route path="/compliance-status" element={
                          <ProtectedRoute>
                            <ComplianceStatus />
                          </ProtectedRoute>
                        } />
                        <Route path="/ticket" element={
                          <ProtectedRoute>
                            <Tickets />
                          </ProtectedRoute>
                        } />
    <Route path="/organization-chart" element={
                          <ProtectedRoute>
                            <OrganizationChart/>
                          </ProtectedRoute>
                        } /> 

                          <Route path="/oneboxMessage" element={
                          <ProtectedRoute>
                           <FileManager />
                          </ProtectedRoute>
                        } />
                        <Route path="/create" element={
                          <ProtectedRoute>
                            <CreateCompliance />
                          </ProtectedRoute>
                        } />
                          <Route path="/catalogs/services" element={
                          <ProtectedRoute>
                            <ServicesCatalogPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/services/create" element={
                          <ProtectedRoute>
                            <ServiceCatalogCreatePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/services/create/:id" element={
                          <ProtectedRoute>
                            <ServiceCatalogCreatePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances/edit/:id" element={
                          <ProtectedRoute>
                            <UpdateCompliance />
                          </ProtectedRoute>
                        } />
                        <Route path="/certifications" element={
                          <ProtectedRoute>
                            <CertificationsList/>
                          </ProtectedRoute>
                        } />

                        <Route path="/compliances/status/:id" element={
                          <ProtectedRoute>
                            <ComplianceStatus/>
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances/view-control/:id/:fieldId/:subFieldId" element={
                          <ProtectedRoute>
                            <ViewControl />
                          </ProtectedRoute>
                        } />
                        <Route path="/login" element={<Login />} />
                        <Route path="/logout-clear" element={<LogoutClear />} />
                        {/* Legacy /compliance/login URL: redirect to canonical /login */}
                        <Route path="/compliance/login" element={<Navigate to="/login" replace />} />
                        <Route path="/register" element={<Register />} />
                        {/* Partner Portal routes removed — moved to the standalone partner_portal_frontend app. */}
                        {/* Pianat Admin routes moved to the standalone pianat_admin_frontend app. */}
                        {/* Phase 2 — Client Portal self-service. */}
                        <Route path="/client-portal/dashboard" element={
                          <ProtectedRoute><ClientPortalDashboard /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/self-assessment" element={
                          <ProtectedRoute><ClientPortalSelfAssessments /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/self-assessment/new" element={
                          <ProtectedRoute><ClientPortalSelfAssessmentNew /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/self-assessment/:id" element={
                          <ProtectedRoute><ClientPortalSelfAssessmentDetail /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/self-assessment/:id/ai-suggestions" element={
                          <ProtectedRoute><ClientPortalSelfAssessmentAi /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/frameworks" element={
                          <ProtectedRoute><ClientPortalFrameworks /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/notifications" element={
                          <ProtectedRoute><ClientPortalNotifications /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/profile" element={
                          <ProtectedRoute><ClientPortalProfile /></ProtectedRoute>
                        } />
                        {/* TrustLens — light-tier onboarding + Posture Cascade. */}
                        <Route path="/trustlens" element={<Navigate to="/trustlens/dashboard" replace />} />
                        <Route path="/trustlens/posture" element={<Navigate to="/trustlens/posture/l1" replace />} />
                        <Route element={<TrustLensPostureLayout />}>
                          <Route path="/trustlens/posture/l1" element={
                            <ProtectedRoute><TrustLensPostureL1 /></ProtectedRoute>
                          } />
                          <Route path="/trustlens/posture/l2" element={
                            <ProtectedRoute><TrustLensPostureL2 /></ProtectedRoute>
                          } />
                          <Route path="/trustlens/posture/l3" element={
                            <ProtectedRoute><TrustLensPostureL3 /></ProtectedRoute>
                          } />
                          <Route path="/trustlens/posture/l4" element={
                            <ProtectedRoute><TrustLensPostureL4 /></ProtectedRoute>
                          } />
                          <Route path="/trustlens/posture/l5" element={
                            <ProtectedRoute><TrustLensPostureL5 /></ProtectedRoute>
                          } />
                        </Route>
                        <Route path="/trustlens/dashboard" element={
                          <ProtectedRoute><TrustLensDashboard /></ProtectedRoute>
                        } />
                        <Route path="/trustlens/profile" element={
                          <ProtectedRoute><TrustLensProfile /></ProtectedRoute>
                        } />
                        <Route path="/trustlens/assessment" element={
                          <ProtectedRoute><TrustLensSectionsTeam /></ProtectedRoute>
                        } />
                        <Route path="/trustlens/report" element={
                          <ProtectedRoute><TrustLensReport /></ProtectedRoute>
                        } />
                        <Route path="/trustlens/invitations" element={
                          <ProtectedRoute><TrustLensInvitations /></ProtectedRoute>
                        } />
                        {/* Client Portal — assessed-org side of the partner lifecycle. */}
                        <Route path="/client-portal/partners" element={
                          <ProtectedRoute><ClientPortalPartners /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/requests" element={
                          <ProtectedRoute><ClientPortalRequests /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/engagements" element={
                          <ProtectedRoute><ClientPortalEngagements /></ProtectedRoute>
                        } />
                        <Route path="/client-portal/engagements/:id" element={
                          <ProtectedRoute><ClientPortalEngagementWorkspace /></ProtectedRoute>
                        } />
                        {/* Phase 5 — Sub-Tenant Operations. Sidebar shows these
                            only for tenants with a parent (tenantDepth > 0);
                            backend enforces participant-based access per row. */}
                        <Route path="/sub-tenant/inter-branch" element={
                          <ProtectedRoute><SubTenantInterBranch /></ProtectedRoute>
                        } />
                        <Route path="/sub-tenant/inter-branch/new" element={
                          <ProtectedRoute><SubTenantInterBranchNew /></ProtectedRoute>
                        } />
                        <Route path="/sub-tenant/inter-branch/:id" element={
                          <ProtectedRoute><SubTenantInterBranchThread /></ProtectedRoute>
                        } />
                        <Route path="/sub-tenant/branch-ops-agent" element={
                          <ProtectedRoute><SubTenantBranchOps /></ProtectedRoute>
                        } />
                        {/* Phase 4 — Tenant Owner Admin. Backend gate is
                            @TenantOwnerOnly (canManageMemberships + subtree
                            scope); the sidebar hides these for everyone else. */}
                        <Route path="/tenant-admin/subtree" element={
                          <ProtectedRoute><TenantAdminSubtree /></ProtectedRoute>
                        } />
                        <Route path="/tenant-admin/memberships" element={
                          <ProtectedRoute><TenantAdminMemberships /></ProtectedRoute>
                        } />
                        <Route path="/tenant-admin/sub-tenants/new" element={
                          <ProtectedRoute><TenantAdminSubTenantNew /></ProtectedRoute>
                        } />
                        <Route path="/tenant-admin/sub-tenants/:id" element={
                          <ProtectedRoute><TenantAdminSubTenantDetail /></ProtectedRoute>
                        } />
                        <Route path="/tenant-admin/rollup" element={
                          <ProtectedRoute><TenantAdminRollup /></ProtectedRoute>
                        } />
                        <Route path="/tenant-admin/rollup/findings" element={
                          <ProtectedRoute><TenantAdminRollupFindings /></ProtectedRoute>
                        } />
                        <Route path="/tenant-admin/rollup/spend" element={
                          <ProtectedRoute><TenantAdminRollupSpend /></ProtectedRoute>
                        } />
                        <Route path="/tenant-admin/insights" element={
                          <ProtectedRoute><TenantAdminInsights /></ProtectedRoute>
                        } />
                        <Route path="/function" element={
                          <ProtectedRoute>
                            <CreateFunctionPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/services" element={
                          <ProtectedRoute>
                            <ServicesPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/document-categories" element={
                          <ProtectedRoute>
                            <DocumentCategoriesPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/tenants/tree" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantTree/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/tenants/tickets" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantTickets/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/tenants/settings" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantSettings/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/tenants/standards" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantStandards/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/tenants/:id" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/debug/tenant-me" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantMeDebug/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/federations" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <FederationHub/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/federations/new" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ServiceProviderRequest/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/federations/:id" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <FederationDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/evidence" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <EvidenceVaultList/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/evidence/:id" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <EvidenceArtifactDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/trust-center" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TrustCenter/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/trust/:slug" element={
                          <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                            <TrustCenterPublic/>
                          </React.Suspense>
                        } />
                        <Route path="/playbooks" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <PlaybookLibrary/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/playbooks/new" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <PlaybookAuthor/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/playbooks/:id" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <PlaybookDetailPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/playbooks/:id/edit" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <PlaybookAuthor/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/llm-routing" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <LlmRouting/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/partner-ledger" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <PartnerLedgerStatement/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        {/* Admin Partners routes removed — see lazy-import block above. */}
                        {/* Partner Portal /partner-portal/* routes (engagements, earnings,
                            practitioners, status) removed — moved to the standalone
                            partner_portal_frontend app. */}
                        <Route path="/risks" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RiskList/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/risks/create" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RiskForm/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/risks/:id" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RiskDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/risks/:id/edit" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RiskForm/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances/:id/links" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ComplianceLinksDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/capabilities/:id/links" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <CapabilityLinksDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/dashboards/roles" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RoleDashboard/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/dashboard" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ConfigurationDashboard/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/services-status" element={
                          <ProtectedRoute>
                            <ServicesStatusPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/lifecycle-stages" element={
                          <ProtectedRoute>
                            <LifecycleStagesPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/criticalities" element={
                          <ProtectedRoute>
                            <CriticalityPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/asset-types" element={
                          <ProtectedRoute>
                            <AssetTypesPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/asset-classifications" element={
                          <ProtectedRoute>
                            <AssetClassificationsPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs" element={
                          <ProtectedRoute>
                            <CatalogsPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/dashboard" element={
                          <ProtectedRoute>
                            <CatalogsDashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/dashboard/risk-controls-matrix" element={
                          <ProtectedRoute>
                            <RiskControlsMatrixPage />
                          </ProtectedRoute>
                        } />
                        {/* Receivables Hub — single page, three tabs */}
                        <Route path="/analytics/receivables" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ReceivablesHubPage />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        {/* Legacy routes preserved so existing links keep working; they open the hub on the relevant tab. */}
                        <Route path="/analytics/collections-dashboard" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ReceivablesHubPage initialTab="overview" />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/analytics/collections-ops" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ReceivablesHubPage initialTab="live" />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/simulation/c2c-monte-carlo" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ReceivablesHubPage initialTab="simulator" />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />



                        
                        <Route path="/catalogs/collection-sops" element={
                          <ProtectedRoute>
                            <SopCatalogPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/sop-masters" element={
                          <ProtectedRoute>
                            <SopMastersPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/data-inventory/dashboard" element={
                          <ProtectedRoute>
                            <DataInventoryDashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/QuestionControlTree" element={
                          <ProtectedRoute>
                            <QuestionControlTree/>
                          </ProtectedRoute>
                        } />
                   <Route path="/viewsurvey" element={
                     <ProtectedRoute>
                       <Surveys/>
                     </ProtectedRoute>
                   } /> 
                      <Route path="/updatesurvey/:id" element={
                        <ProtectedRoute>
                          <UpdateSurvey/>
                        </ProtectedRoute>
                      } />
                      <Route path="/updateSurvey/:id" element={
                        <ProtectedRoute>
                          <UpdateSurvey/>
                        </ProtectedRoute>
                      } />
                        <Route path="/control" element={
                        <ProtectedRoute>
                          <ControlsIndex/>
                        </ProtectedRoute>
                      } />
                        <Route path="/controls" element={
                        <ProtectedRoute>
                          <ControlsIndex/>
                        </ProtectedRoute>
                      } />
                        <Route path="/controls/create" element={
                        <ProtectedRoute>
                          <CreateControlPage/>
                        </ProtectedRoute>
                      } />
                        <Route path="/controls/:id" element={
                        <ProtectedRoute>
                          <ControlPage/>
                        </ProtectedRoute>
                      } />
                        <Route path="/user" element={
                          <ProtectedRoute>
                            <UserManage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/user/permissions" element={
                          <ProtectedRoute>
                            <UserPermissionManage/>
                          </ProtectedRoute>
                        } /> 
                        <Route path="/permission/groups-pages" element={
                          <ProtectedRoute>
                            <PermissionGroupPageManage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/notification" element={
                          <ProtectedRoute>
                            <Notification/>
                          </ProtectedRoute>
                        } /> 
           <Route path="/team-onboarding" element={
             <ProtectedRoute>
               <TeamOnboarding />
             </ProtectedRoute>
           } />

                        <Route path="/exportsurvey/:id" element={
                          <ProtectedRoute>
                            <ExportSurvey/>
                          </ProtectedRoute>
                        } /> 
                        <Route path="/assessmentsurvey/:id" element={
                          <ProtectedRoute>
                            <ReplayQuestion/>
                          </ProtectedRoute>
                        } /> 
                        <Route path="/objective" element={
                          <ProtectedRoute>
                            <ObjectiveModal/>
                          </ProtectedRoute>
                        } /> 



<Route path="/SurveyCategoryQuestionManager" element={
                            <ProtectedRoute>
                                <SurveyCategoryQuestionManager/>
                            </ProtectedRoute>
                        } />
                        <Route path="/category" element={
                            <ProtectedRoute>
                                <CategoryTable/>
                            </ProtectedRoute>
                        } /> 
 <Route path="/questionassessment" element={
                            <ProtectedRoute>
                                <ViewQuestionsTable/>
                            </ProtectedRoute>
                        } /> 

<Route path="/ctreatQuestionAssessment" element={
                            <ProtectedRoute>
                                <CreateQuestionForm/>
                            </ProtectedRoute>
                        } /> 
                         <Route path="/reportSup" element={
                          <ProtectedRoute>
                            <ReportSupCompliance/>
                          </ProtectedRoute>
                        } /> 
  <Route path="/assign-certifications/:id" element={
                            <ProtectedRoute>
                                <CertificateSigningApp/>
                            </ProtectedRoute>
                        } />


<Route path="/institutionSup" element={
                          <ProtectedRoute>
                            <InstitutionSupHome/>
                          </ProtectedRoute>
                        } />
                        <Route path="/institutionSup/child-operations/:tenantId" element={
                          <ProtectedRoute>
                            <ChildTenantFieldOperations />
                          </ProtectedRoute>
                        } />


                        <Route path="/assessment-survey/:id" element={<ReplayQuestion/>} /> 

      <Route path="/updateQuestion/:id" element={<UpdateQuestion/>} />
                           <Route path="/createQuestion" element={<CreateQuestion/>} /> 
                        <Route path="/create-survey" element={<CreateSurvey/>} /> 
                        <Route path="/export-survey/:id" element={<ExportSurvey/>} /> 
                  
                          <Route path="/surveys" element={<Surveys/>} /> 
                        <Route path="/bankQuestions" element={<QuestionBankTable/>} /> 
                        <Route path="/frameWork" element={
                          <ProtectedRoute>
                            <UnifiedControlLandscape/>
                          </ProtectedRoute>
                        } />
                        <Route path="/assessment-score" element={
                          <ProtectedRoute>
                            <AssessmentScore/>
                          </ProtectedRoute>
                        } /> 

                     
                        <Route path="/controls/update/:id" element={
                            <ControlUpdatePage/>
                        } />

                        <Route path="/viewTaskCalender" element={
                          <ProtectedRoute>
                            <TaskUserCalender/>
                          </ProtectedRoute>
                        } /> 
                        <Route path="/permission" element={
                          <ProtectedRoute>
                            <PermissionPages/>
                          </ProtectedRoute>
                        } />
                        {/* Subscribe & Zoho subscribe - commented out
                        <Route path="/subscription" element={
                          <ProtectedRoute>
                            <SubscriptionPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/renew-subscription" element={
                          <ProtectedRoute>
                            <RenewSubscriptionPage/>
                          </ProtectedRoute>
                        } />
                        */}
                        {/* Resources (formerly Assets) under Catalog */}
                        <Route path="/catalogs/resources" element={
                          <ProtectedRoute>
                            <AssetsList />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/resources/create" element={
                          <ProtectedRoute>
                            <CreateAsset />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/resources/:id" element={
                          <ProtectedRoute>
                            <AssetDetails />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/resources/edit/:id" element={
                          <ProtectedRoute>
                            <EditAsset />
                          </ProtectedRoute>
                        } />
                        {/* Redirect legacy /assets paths to /catalogs/resources */}
                        <Route path="/assets/create" element={<Navigate to="/catalogs/resources/create" replace />} />
                        <Route path="/assets/edit/:id" element={<ProtectedRoute><RedirectAssetsToResources /></ProtectedRoute>} />
                        <Route path="/assets/:id" element={<ProtectedRoute><RedirectAssetsToResources /></ProtectedRoute>} />
                        <Route path="/assets" element={<Navigate to="/catalogs/resources" replace />} />

                        {/* Enterprise Architecture Routes */}
                        <Route path="/ea/dashboard" element={
                          <ProtectedRoute>
                            <EADashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/ea/principles" element={
                          <ProtectedRoute>
                            <EAPrinciples />
                          </ProtectedRoute>
                        } />
                           <Route path="/impactarea" element={
                          <ProtectedRoute>
                          <ImpactAreaTable />
                          </ProtectedRoute>
                        } />
                        <Route path="/ea/applications" element={
                          <ProtectedRoute>
                            <EAApplications />
                          </ProtectedRoute>
                        } />
                         <Route path="/rcsa-compliance" element={
                          <ProtectedRoute>
                            <RcsaCatalogCompliance />
                          </ProtectedRoute>
                        } />

<Route path="/standardsSupCompliance" element={
                          <ProtectedRoute>
                            <StandardsSupCompliance />
                          </ProtectedRoute>
                        } />
                        <Route path="/certificationsSup" element={
                          <ProtectedRoute>
                            <CertificationsListSup />
                          </ProtectedRoute>
                        } />

<Route path="/announcementSup" element={
                          <ProtectedRoute>
                            <AnnouncementSup/>
                          </ProtectedRoute>
                        } />


<Route path="/dashboardSup" element={
                          <ProtectedRoute>
                            <DashboardSup />
                          </ProtectedRoute>
                        } />


<Route path="/assessmentSup" element={
                          <ProtectedRoute>
                            <IntialAssessmentSup />
                          </ProtectedRoute>
                        } />


                        <Route path="/ea/work-packages" element={
                          <ProtectedRoute>
                            <EAWorkPackages />
                          </ProtectedRoute>
                        } />
                        <Route path="/ea/scope-items" element={
                          <ProtectedRoute>
                            <EAScopeItems />
                          </ProtectedRoute>
                        } />
                        <Route path="/ea/relations-tree" element={
                          <ProtectedRoute>
                            <EARelationsTree />
                          </ProtectedRoute>
                        } />

                        {/* EA Strategic Canvas (Comply.Now hub — same UX as enterprise-compliance-hub) */}
                        <Route path="/ea-canvas/*" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={
                              <div className="d-flex justify-content-center align-items-center p-5 w-100">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            }>
                              <EaCanvasModule />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />

                        {/* Process Module (from process project) - paths aligned with process app */}
                        <Route path="/process-builder" element={
                          <ProtectedRoute>
                            <ProcessBuilder />
                          </ProtectedRoute>
                        } />
                        <Route path="/modeler" element={
                          <ProtectedRoute>
                            <ProcessModeler />
                          </ProtectedRoute>
                        } />
                        <Route path="/instances" element={
                          <ProtectedRoute>
                            <Instances />
                          </ProtectedRoute>
                        } />
                        <Route path="/instances/:id" element={
                          <ProtectedRoute>
                            <InstanceDetail />
                          </ProtectedRoute>
                        } />
                        <Route path="/tasks" element={
                          <ProtectedRoute>
                            <TaskInbox />
                          </ProtectedRoute>
                        } />
                        <Route path="/monitor" element={
                          <ProtectedRoute>
                            <LiveMonitor />
                          </ProtectedRoute>
                        } />
                        <Route path="/agents" element={
                          <ProtectedRoute>
                            <AgentConfig />
                          </ProtectedRoute>
                        } />
                        <Route path="/metrics" element={
                          <ProtectedRoute>
                            <MetricsDashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/metrics/definitions" element={
                          <ProtectedRoute>
                            <MetricDefinitions />
                          </ProtectedRoute>
                        } />
                        <Route path="/metrics/process/:processId" element={
                          <ProtectedRoute>
                            <ProcessAnalytics />
                          </ProtectedRoute>
                        } />
                        <Route path="/process/controls" element={<Navigate to="/process/viewer" replace />} />
                        <Route path="/process/viewer" element={
                          <ProtectedRoute>
                            <ProcessInstanceViewer />
                          </ProtectedRoute>
                        } />
                        <Route path="/process/:id" element={
                          <ProtectedRoute>
                            <ProcessDetailPage />
                          </ProtectedRoute>
                        } />
                        {/* Legacy process paths → redirect to new paths */}
                        <Route path="/process/instances" element={<Navigate to="/instances" replace />} />
                        <Route path="/process/instances/:id" element={<RedirectProcessInstanceId />} />
                        <Route path="/process/monitor" element={<Navigate to="/monitor" replace />} />
                        <Route path="/process/agents" element={<Navigate to="/agents" replace />} />

                        {/* Policy / SOP Engine (from process project) */}
                        <Route path="/policy" element={
                          <ProtectedRoute>
                            <div className="policy-page-container">
                              <PolicyLibrary />
                            </div>
                          </ProtectedRoute>
                        } />
                        <Route path="/policy/categories" element={
                          <ProtectedRoute>
                            <div className="policy-page-container">
                              <PolicyCategories />
                            </div>
                          </ProtectedRoute>
                        } />
                        <Route path="/policy/obligations" element={
                          <ProtectedRoute>
                            <div className="policy-page-container">
                              <RegulatoryObligations />
                            </div>
                          </ProtectedRoute>
                        } />
                        <Route path="/policy/approvals" element={
                          <ProtectedRoute>
                            <div className="policy-page-container">
                              <PolicyApprovals />
                            </div>
                          </ProtectedRoute>
                        } />
                        <Route path="/policy/attestation" element={
                          <ProtectedRoute>
                            <div className="policy-page-container">
                              <PolicyAttestation />
                            </div>
                          </ProtectedRoute>
                        } />
                        <Route path="/policy/versions" element={
                          <ProtectedRoute>
                            <div className="policy-page-container">
                              <PolicyVersionControl />
                            </div>
                          </ProtectedRoute>
                        } />
                        <Route path="/policy/audit" element={
                          <ProtectedRoute>
                            <div className="policy-page-container">
                              <PolicyAudit />
                            </div>
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/activity-log" element={
                          <ProtectedRoute>
                            <ActivityLogPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/iams" element={
                          <ProtectedRoute>
                            <IamsDashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/iams/canvas" element={
                          <ProtectedRoute>
                            <IamsLiveCanvas />
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/iams/universe" element={<ProtectedRoute><IamsAuditUniverse /></ProtectedRoute>} />
                        <Route path="/audit/iams/plan-composer" element={<ProtectedRoute><IamsPlanComposer /></ProtectedRoute>} />
                        <Route path="/audit/iams/fieldwork" element={<ProtectedRoute><IamsFieldworkCanvas /></ProtectedRoute>} />
                        <Route path="/audit/iams/findings/:id" element={<ProtectedRoute><IamsFindingDetail /></ProtectedRoute>} />
                        <Route path="/audit/iams/audit-trail/:entityType/:entityId" element={<ProtectedRoute><IamsAuditTrailViewer /></ProtectedRoute>} />
                        <Route path="/audit/iams/anomaly-triage" element={<ProtectedRoute><IamsAnomalyTriage /></ProtectedRoute>} />
                        <Route path="/audit/iams/risk-updates" element={<ProtectedRoute><IamsRiskUpdateQueue /></ProtectedRoute>} />
                        <Route path="/audit/iams/admin" element={<ProtectedRoute><IamsAdmin /></ProtectedRoute>} />
                        <Route path="/audit/iams/engagement-report" element={<ProtectedRoute><IamsEngReportBuilder /></ProtectedRoute>} />
                        <Route path="/audit/iams/ac-pack" element={<ProtectedRoute><IamsAcPackBuilder /></ProtectedRoute>} />
                        <Route path="/audit/iams/board-pack" element={<ProtectedRoute><IamsBoardPackBuilder /></ProtectedRoute>} />
                        <Route path="/audit/iams/ac-dashboard" element={<ProtectedRoute><IamsAcDashboard /></ProtectedRoute>} />
                        <Route path="/audit/iams/auditee-portal" element={<ProtectedRoute><IamsAuditeePortal /></ProtectedRoute>} />
                        <Route path="/audit/iams/auditee-requests" element={<ProtectedRoute><IamsAuditeeRequests /></ProtectedRoute>} />
                        <Route path="/audit/iams/auditee-notify" element={<ProtectedRoute><IamsAuditeeNotify /></ProtectedRoute>} />
                        <Route path="/audit/iams/evidence" element={<ProtectedRoute><IamsEvidenceLocker /></ProtectedRoute>} />
                        <Route path="/audit/iams/capacity" element={<ProtectedRoute><IamsCapacityPlanner /></ProtectedRoute>} />
                        <Route path="/audit/iams/working-paper" element={<ProtectedRoute><IamsWorkingPaperEditor /></ProtectedRoute>} />
                        <Route path="/audit/iams/test-plan" element={<ProtectedRoute><IamsTestPlanComposer /></ProtectedRoute>} />
                        <Route path="/audit/iams/monitor" element={<ProtectedRoute><IamsContinuousMonitor /></ProtectedRoute>} />
                        <Route path="/audit/iams/ac-plan-review" element={<ProtectedRoute><IamsAcPlanReview /></ProtectedRoute>} />
                        <Route path="/audit/iams/copilot-connector" element={<ProtectedRoute><IamsCopilotConnector /></ProtectedRoute>} />
                        <Route path="/audit/iams/trust-center" element={<ProtectedRoute><IamsTrustCenter /></ProtectedRoute>} />
                        <Route path="/audit/iams/admin/import" element={<ProtectedRoute><IamsBulkImport /></ProtectedRoute>} />
                        <Route path="/audit/iams/checklists" element={<ProtectedRoute><IamsChecklists /></ProtectedRoute>} />
                        <Route path="/audit/iams/rc-library" element={<ProtectedRoute><IamsRcLibrary /></ProtectedRoute>} />
                        <Route path="/audit/iams/capacity/:auditorId" element={<ProtectedRoute><IamsResourceProfile /></ProtectedRoute>} />
                        <Route path="/audit/iams/admin/workflows" element={<ProtectedRoute><IamsWorkflowDesigner /></ProtectedRoute>} />
                        <Route path="/audit/iams/analytics" element={<ProtectedRoute><IamsAnalytics /></ProtectedRoute>} />
                        <Route path="/audit/iams/scenario-planner" element={<ProtectedRoute><IamsScenarioPlanner /></ProtectedRoute>} />
                        <Route path="/audit/iams/engagements" element={
                          <ProtectedRoute>
                            <IamsEngagements />
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/iams/engagements/:id" element={
                          <ProtectedRoute>
                            <IamsEngagementDetail />
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/iams/review-queue" element={
                          <ProtectedRoute>
                            <IamsReviewQueue />
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/iams/findings" element={
                          <ProtectedRoute>
                            <IamsFindings />
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/iams/action-plans" element={
                          <ProtectedRoute>
                            <IamsActionPlans />
                          </ProtectedRoute>
                        } />
                        <Route path="/audit/iams/agent-runs" element={
                          <ProtectedRoute>
                            <IamsAgentRuns />
                          </ProtectedRoute>
                        } />
                        <Route path="/policy/settings" element={
                          <ProtectedRoute>
                            <div className="policy-page-container">
                              <PolicySettings />
                            </div>
                          </ProtectedRoute>
                        } />

                        {/* User Guide (from process project) */}
                        <Route path="/guide" element={
                          <ProtectedRoute>
                            <UserGuide />
                          </ProtectedRoute>
                        } />
                        <Route path="/ea/work-package-risks" element={<Navigate to="/ea/work-packages" replace />} />

                        <Route path="/dataresource" element={
                          <ProtectedRoute>
                            <DataSourceList />
                          </ProtectedRoute>
                        } />
                        <Route path="/dataresource/:id" element={
                          <ProtectedRoute>
                            <DataSourceDetails />
                          </ProtectedRoute>
                        } />
                        <Route path="/dataresource/update/:id" element={
                          <ProtectedRoute>
                            <UpdateDataSource />
                          </ProtectedRoute>
                        } />
                        {/* Document Management Routes */}
                        <Route path="/documents" element={
                          <ProtectedRoute>
                            <DocumentList />
                          </ProtectedRoute>
                        } />
                        <Route path="/documents/upload" element={
                          <ProtectedRoute>
                            <DocumentUpload />
                          </ProtectedRoute>
                        } />
                        <Route path="/documents/:id/edit" element={
                          <ProtectedRoute>
                            <DocumentEdit />
                          </ProtectedRoute>
                        } />

                        <Route path="/assessment" element={
                          <ProtectedRoute>
                            <UpdateAssessment/>
                          </ProtectedRoute>
                        } />
                             <Route path="/subBusinessUnit" element={
                          <ProtectedRoute>
                            <CreateSubBusinessUnitPage/>
                          </ProtectedRoute>
                        } />



                      <Route path="/jobTitle" element={
                          <ProtectedRoute>
                            <CreateJobTitlePage/>
                          </ProtectedRoute>
                        } />

<Route path="/createCurrency" element={
                          <ProtectedRoute>
                            <CreateCurrencyPage/>
                          </ProtectedRoute>
                        } />

                     <Route path="/discoveredTypes" element={
                          <ProtectedRoute>
                            <CreateDiscoveredTypePage/>
                          </ProtectedRoute>
                        } />

                       <Route path="/rootCause" element={
                          <ProtectedRoute>
                            <CreateRootCausePage/>
                          </ProtectedRoute>
                        } />

                     <Route path="/system" element={
                          <ProtectedRoute>
                            <CreateSystemPage/>
                          </ProtectedRoute>
                        } />

    
<Route path="/subFunction" element={
                          <ProtectedRoute>
                            <CreateSubFunctionPage/>
                          </ProtectedRoute>
                        } />
    
    <Route path="/activities" element={
                          <ProtectedRoute>
                            <CreateActivityPage/>
                          </ProtectedRoute>
                        } />
                        
<Route path="/riskCategory" element={
                          <ProtectedRoute>
                            <CreateRiskCategoryPage/>
                          </ProtectedRoute>
                        } />
      
      <Route path="/controlReference" element={
                          <ProtectedRoute>
                            <ControlReferencePage/>
                          </ProtectedRoute>
                        } />

<Route path="/accountType" element={
                          <ProtectedRoute>
                            <CreateAccountType/>
                          </ProtectedRoute>
                        } />
                        
      <Route path="/eventType" element={
                          <ProtectedRoute>
                            <EventTypePage/>
                          </ProtectedRoute>
                        } />



<Route path="/financialImpacts" element={
                          <ProtectedRoute>
                            <FinancialImpactsPage/>
                          </ProtectedRoute>
                        } />




                       <Route path="/process" element={
                          <ProtectedRoute>
                            <CreateProcessPage/>
                          </ProtectedRoute>
                        } />

                        <Route path="/documents/:id" element={
                          <ProtectedRoute>
                            <DocumentDetails />
                          </ProtectedRoute>
                        } />
                        <Route path="/documents/dashboard" element={
                          <ProtectedRoute>
                            <ComplianceDashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/documents/notifications" element={
                          <ProtectedRoute>
                            <Notification />
                          </ProtectedRoute>
                        } />


<Route path="/ticketsSupCompliance" element={
                          <ProtectedRoute>
                          <TicketsSupCompliance/>
                          </ProtectedRoute>
                        } />







<Route path="/createImpact" element={<CreateImpact/>} /> 
                        <Route path="/updateImpacted/:id" element={<UpdateImpact/>} /> 

                        <Route path="/documents/structure" element={
                          <ProtectedRoute>
                            <DocumentStructurePage />
                          </ProtectedRoute>
                        } />
                        {/*<Route path="/impactArea" element={<ImpactAreaTable/>} /> 
                        <Route path="/createImpact" element={<CreateImpact/>} /> 
                        <Route path="/updateImpacted/:id" element={<UpdateImpact/>} /> 


                        <Route path="/assessment-survey/:id" element={<ReplayQuestion/>} /> 
                        <Route path="/updateSurvey/:id" element={<UpdateSurvey/>} /> */}





                        

                    </Routes>
                    </FederationGate>
                    </ModuleGate>
                </React.Suspense>
                </div>
                </div>
                </PageHeadingProvider>
                {!isLoginPage && <AppFooter />}
            </div>
        </div>
    );
};

export default Main;















